import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import Intro from './components/Intro.jsx';
import Quiz from './components/Quiz.jsx';
import Results from './components/Results.jsx';
import PurposeSelection from './components/PurposeSelection.jsx';
import MissionDashboard from './components/MissionDashboard.jsx';
import LearningContract from './components/LearningContract.jsx';
import RegistrationForm from './components/RegistrationForm.jsx';
import SuccessStep from './components/SuccessStep.jsx';

// Steps: 0=Intro 1=Quiz 2=Results 3=Purpose 4=Dashboard 5=Contract 6=Registration 7=Success
const TOTAL_STEPS = 8;

export default function App() {
  const [step, setStep] = useState(0);
  const [scores, setScores] = useState(null);
  const [dominantMode, setDominantMode] = useState('structure');
  const [dominantTool, setDominantTool] = useState('systematic');
  const [purpose, setPurpose] = useState('sprint');
  const [userData, setUserData] = useState(null);

  useEffect(() => {
  const reportHeight = () => {
    const h = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight,
      document.body.clientHeight,
      document.documentElement.clientHeight
    );

    window.parent.postMessage(
      { type: 'mathemission-height', height: h },
      'https://digikreide-kurse.de'
    );
  };

  reportHeight();

  const observer = new ResizeObserver(reportHeight);
  observer.observe(document.body);

  window.addEventListener('load', reportHeight);
  window.addEventListener('resize', reportHeight);

  const t1 = setTimeout(reportHeight, 100);
  const t2 = setTimeout(reportHeight, 500);

  return () => {
    observer.disconnect();
    window.removeEventListener('load', reportHeight);
    window.removeEventListener('resize', reportHeight);
    clearTimeout(t1);
    clearTimeout(t2);
  };
}, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    const t = setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: 'instant' }), 50);
    return () => clearTimeout(t);
  }, [step]);

  const handleQuizComplete = (finalScores) => {
    setScores(finalScores);
    const mode = Object.keys(finalScores.mode).reduce((a, b) =>
      finalScores.mode[a] > finalScores.mode[b] ? a : b
    );
    const tool = Object.keys(finalScores.tool).reduce((a, b) =>
      finalScores.tool[a] > finalScores.tool[b] ? a : b
    );
    setDominantMode(mode);
    setDominantTool(tool);
    setStep(2);
  };

  const handleRegistrationComplete = (data) => {
    setUserData(data);
    setStep(7);
  };

  const nextStep = () => setStep((prev) => Math.min(prev + 1, TOTAL_STEPS - 1));

  return (
    <div
      className="min-h-screen text-slate-50 font-sans selection:bg-blue-500/30 overflow-x-hidden relative"
      style={{ background: 'linear-gradient(180deg,#34495e,#2c3e50)' }}
    >
      {/* Background Glows */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-5%] w-[40%] h-[40%] bg-blue-400/10 blur-[100px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[40%] h-[40%] bg-slate-400/10 blur-[100px] rounded-full" />
      </div>

      {/* Header */}
      <header className="relative z-10 w-full p-6 flex justify-between items-center max-w-7xl mx-auto">
        <div className="flex items-center gap-3">
          <img
            src="https://kanban.digikreide-kurse.de/logo.png"
            alt="MatheMission Logo"
            className="h-10 w-auto object-contain"
          />
          <span className="text-xl font-bold tracking-tight">
            Mathe<span className="text-slate-200">Mission</span>
          </span>
        </div>
        {step > 0 && (
          <div className="flex gap-2">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  step >= i ? 'bg-blue-400 w-10' : 'bg-white/20 w-6'
                }`}
              />
            ))}
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 pb-24">
        <AnimatePresence mode="wait">
          {step === 0 && <Intro key="intro" onStart={nextStep} />}
          {step === 1 && <Quiz key="quiz" onComplete={handleQuizComplete} />}
          {step === 2 && scores && (
            <Results key="results" scores={scores} onNext={nextStep} />
          )}
          {step === 3 && (
            <PurposeSelection
              key="purpose"
              selectedId={purpose}
              onSelect={setPurpose}
              onNext={nextStep}
            />
          )}
          {step === 4 && (
            <MissionDashboard
              key="dashboard"
              modeId={dominantMode}
              purposeId={purpose}
              toolId={dominantTool}
              onNext={nextStep}
            />
          )}
          {step === 5 && (
            <LearningContract
              key="contract"
              modeId={dominantMode}
              purposeId={purpose}
              toolId={dominantTool}
              onNext={nextStep}
            />
          )}
          {step === 6 && (
            <RegistrationForm
              key="registration"
              modeId={dominantMode}
              purposeId={purpose}
              toolId={dominantTool}
              scores={scores}
              onComplete={handleRegistrationComplete}
            />
          )}
          {step === 7 && <SuccessStep key="success" />}
        </AnimatePresence>
      </main>
    </div>
  );
}